import React from 'react'
import { format } from 'date-fns'
import { Button } from '../../components/ui'

// Kalenderens synlige timer og høyde per time (px)
export const DAY_START_HOUR = 6
export const DAY_END_HOUR = 24
export const HOUR_HEIGHT = 52
export const SNAP_MINUTES = 15

/*
 * Hallfarger. Fargene skiller seg i lyshet (mørk / lys / mellom), ikke bare i kulør,
 * og hver farge har en tekstfarge med kontrast ≥ 4.5:1.
 * bg = fyll, fg = tekst, sub = sekundærtekst, bd = kant (synlig også på hvit bunn).
 */
export const COURT_PALETTE = [
  { bg: '#0F1511', fg: '#FFFFFF', sub: '#C9CEC6', bd: '#0F1511' }, // blekk (mørk)
  { bg: '#C4F135', fg: '#0F1511', sub: '#0F1511', bd: '#9DC21F' }, // lime (lys)
  { bg: '#FF6A2B', fg: '#0F1511', sub: '#0F1511', bd: '#FF6A2B' }, // signal (mellom)
  { bg: '#2D5BFF', fg: '#FFFFFF', sub: '#FFFFFF', bd: '#2D5BFF' }, // blå (mellom-mørk)
  { bg: '#EEF9C9', fg: '#3F5A00', sub: '#3F5A00', bd: '#3F5A00' }, // lime-soft (svært lys, mørk kant)
  { bg: '#5A6258', fg: '#FFFFFF', sub: '#E9EBE3', bd: '#5A6258' }, // grå (mørk)
  { bg: '#FFE6DA', fg: '#B23D0A', sub: '#B23D0A', bd: '#FF6A2B' }, // signal-soft (svært lys)
  { bg: '#DCE5FF', fg: '#1C3BB3', sub: '#1C3BB3', bd: '#2D5BFF' } // blå-soft (svært lys)
]
export const NO_COURT_STYLE = { bg: '#E9EBE3', fg: '#0F1511', sub: '#5A6258', bd: '#C9CDC1' }

export const WEEKDAYS = [
  { value: 1, label: 'Man' },
  { value: 2, label: 'Tir' },
  { value: 3, label: 'Ons' },
  { value: 4, label: 'Tor' },
  { value: 5, label: 'Fre' },
  { value: 6, label: 'Lør' },
  { value: 7, label: 'Søn' }
]

export async function readError(res, fallback) {
  try {
    const data = await res.json()
    return data.message || fallback
  } catch (_) {
    return fallback
  }
}

export const pad = (n) => String(n).padStart(2, '0')
export const toLocalInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
export const toDateInput = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
export const fmtTime = (date) => format(date, 'HH:mm')
export const fmtPrice = (ore) => (ore > 0 ? `${(ore / 100).toLocaleString('nb-NO')} kr` : 'Gratis')

/** Legger overlappende utleier side om side i en kolonne. */
export function layoutEvents(events) {
  const sorted = [...events].sort((a, b) => a.start - b.start || b.end - a.end)
  const result = []
  let cluster = []
  let clusterEnd = 0
  const flush = () => {
    const lanes = []
    cluster.forEach((ev) => {
      let lane = lanes.findIndex((end) => end <= ev.start)
      if (lane === -1) {
        lane = lanes.length
        lanes.push(ev.end)
      } else {
        lanes[lane] = ev.end
      }
      ev.lane = lane
    })
    cluster.forEach((ev) => result.push({ ...ev, laneCount: lanes.length }))
    cluster = []
  }
  sorted.forEach((ev) => {
    if (cluster.length && ev.start >= clusterEnd) flush()
    cluster.push(ev)
    clusterEnd = Math.max(clusterEnd, ev.end)
  })
  if (cluster.length) flush()
  return result
}

export function slotToForm(slot) {
  return {
    title: slot.title,
    courtId: slot.courtId || '',
    visibility: slot.visibility || 'FOLLOWERS',
    startsAt: toLocalInput(new Date(slot.startsAt)),
    endsAt: toLocalInput(new Date(slot.endsAt)),
    capacity: slot.capacity,
    price: slot.priceOre / 100,
    cancellationDeadlineHours: slot.cancellationDeadlineHours,
    locationText: slot.locationText || '',
    description: slot.description || ''
  }
}

export function formToPayload(form) {
  return {
    title: form.title.trim(),
    courtId: form.courtId || null,
    visibility: form.visibility,
    startsAt: new Date(form.startsAt).toISOString(),
    endsAt: new Date(form.endsAt).toISOString(),
    capacity: Number(form.capacity) || 1,
    priceOre: Math.round((Number(form.price) || 0) * 100),
    cancellationDeadlineHours: Number(form.cancellationDeadlineHours) || 0,
    locationText: form.locationText.trim() || null,
    description: form.description.trim() || null
  }
}

/** Liten fargeprøve for en hall. */
export const Swatch = ({ style, size = 14 }) => (
  <span
    aria-hidden="true"
    className="rt-swatch"
    style={{ width: size, height: size, background: style.bg, borderColor: style.bd }}
  />
)

/** Bekreftelse som vises der handlingen skjer (samme tekst som før). */
export const InlineConfirm = ({ message, confirmLabel, onConfirm, onCancel, busy, label = 'Bekreft', className }) => (
  <div role="alertdialog" aria-label={label} className={`inline-confirm rt-confirm ${className || ''}`}>
    <span className="rt-confirm-text">{message}</span>
    <div className="rt-confirm-actions">
      <Button variant="tertiary" size="sm" onClick={onCancel}>Avbryt</Button>
      <Button variant="danger" size="sm" onClick={onConfirm} disabled={busy} autoFocus>{confirmLabel}</Button>
    </div>
  </div>
)
